import React, { useState, useEffect } from 'react';
import {
  Accordion, Table, Badge, Alert, Form, Row, Col, Button
} from 'react-bootstrap';
import { FaCheck } from 'react-icons/fa';
import axios from 'axios';

const GDPRRetentionTab = ({ userData, onScoreUpdate, showNotification, region, refreshActivities, logToAccessLogs }) => {
  const [retentionPolicies, setRetentionPolicies] = useState([]);
  const [upcomingDeletions, setUpcomingDeletions] = useState([]);
  const [newPolicy, setNewPolicy] = useState({
    category: '', periodValue: '', periodUnit: 'Years',
    legalBasis: '', autoDelete: false
  });

  const headers = {
    Region: region,
    UserId: userData?.['cognito:username']
  };

  const logActivity = async (message, badge = 'info') => {
    try {
      const payload = { action: badge, module: 'Data Retention', details: message };
      await axios.post(
        'https://w8wvx2z3f4.execute-api.ap-southeast-2.amazonaws.com/prod/api/activity-log/save',
        payload,
        { headers }
      );
      if (refreshActivities) await refreshActivities();
    } catch (err) {
      console.warn('Failed to log activity:', err.message);
    }
  };

  useEffect(() => { fetchPolicies(); }, []);

  const fetchPolicies = async () => {
    try {
      const policyRes = await axios.get(
        'https://6d5x3n4sz8.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/retention-policy/list',
        { headers }
      );
      setRetentionPolicies(policyRes.data);

      const deletionRes = await axios.get(
        'https://nld6l0vshf.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/retention-policy/scheduled-deletions',
        { headers }
      );
      setUpcomingDeletions(deletionRes.data);

      const MIN_CATEGORIES = ['Customer Personal Data', 'Employee Records', 'Marketing Data', 'Transaction Records', 'Website Analytics'];
      const MAX_RETENTION_YEARS = 6;
      const totalPolicies = policyRes.data.length;
      const fullyConfigured = policyRes.data.filter(p => p.category && p.legalBasis && p.periodValue > 0).length;
      const overdueRetention = policyRes.data.filter(p => p.periodUnit === 'Years' && p.periodValue > MAX_RETENTION_YEARS).length;
      const missingCategories = MIN_CATEGORIES.filter(cat => !policyRes.data.some(p => p.category === cat)).length;

      let score = 0;
      if (totalPolicies > 0) {
        score = 100;
        score -= missingCategories * 15;
        score -= overdueRetention * 20;
        score -= (totalPolicies - fullyConfigured) * 10;
        score = Math.max(0, Math.min(100, score));
      }
      onScoreUpdate(Math.round(score));
    } catch (error) {
      console.error('Error fetching policies or deletions', error);
    }
  };

  const handleNewPolicyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewPolicy((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePolicySubmit = async (e) => {
    e.preventDefault();
    const { category, periodValue, periodUnit, legalBasis, autoDelete } = newPolicy;
    const numVal = Number(periodValue);

    if (!category || !numVal || numVal <= 0 || !legalBasis) {
      showNotification('Please fill all required fields', 'danger');
      return;
    }

    const payload = { category: category.trim(), periodValue: numVal, periodUnit, legalBasis, autoDelete };

    try {
      await axios.post(
        'https://p765mmmm4d.execute-api.ap-southeast-2.amazonaws.com/prod/api/gdpr/retention-policy/create',
        payload,
        { headers }
      );
      await logActivity(`Created data retention policy: ${payload.category} â€” ${payload.periodValue} ${payload.periodUnit}`, 'success');
      logToAccessLogs?.({
        action:      'Retention Policy Created',
        severity:    'success',
        description: `Created GDPR retention policy for ${payload.category} â€” ${payload.periodValue} ${payload.periodUnit}`,
        metadata: {
          category:    payload.category,
          periodValue: payload.periodValue,
          periodUnit:  payload.periodUnit,
          legalBasis:  payload.legalBasis,
          autoDelete:  payload.autoDelete,
        }
      });

      await fetchPolicies();
      showNotification('Retention policy created successfully!');
      setNewPolicy({ category: '', periodValue: '', periodUnit: 'Years', legalBasis: '', autoDelete: false });
    } catch (error) {
      console.error('Error saving policy:', error);
      showNotification('Error saving policy', 'danger');
    }
  };

  const [activeAccordionKey, setActiveAccordionKey] = useState(null);
  

  return (
    <>
      <div className="border rounded">
  <div
    className="d-flex justify-content-between align-items-center p-3"
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "1" ? null : "1")
    }
  > 
    <h6>Active Retention Policies</h6>
    <span>{activeAccordionKey === "1" ? "â–²" : "â–¼"}</span>
  </div>

  {activeAccordionKey === "1" && (
    <div className="p-3 border-top">
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Data Category</th>
                  <th>Retention Period</th>
                  <th>Legal Basis</th>
                  <th>Auto-Delete</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {retentionPolicies.map((policy, index) => (
                  <tr key={index}>
                    <td>{policy.category}</td>
                    <td>{policy.periodValue} {policy.periodUnit}</td>
                    <td>{policy.legalBasis}</td>
                    <td>{policy.autoDelete ? <FaCheck className="text-success" /> : 'â€”'}</td>
                    <td>
                      <Badge bg="success">{policy.status || 'Active'}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
  )}

        <div
    className="d-flex justify-content-between align-items-center p-3"
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "0" ? null : "0")
    }
  >
    <h6>Scheduled Deletions</h6>
    <span>{activeAccordionKey === "0" ? "â–²" : "â–¼"}</span>
  </div>

  {activeAccordionKey === "0" && (
    <div className="p-3 border-top">
            <Alert variant="info">
              <strong>Upcoming Deletions:</strong>
              {upcomingDeletions.map((item, index) => (
                <div key={index}>
                  {item.category} â†’ Deletion on {item.nextDeletionDate}
                </div>
              ))}
            </Alert>
          </div>
  )}

        <div
    className="d-flex justify-content-between align-items-center p-3"
    style={{ cursor: "pointer" }}
    onClick={() =>
      setActiveAccordionKey(activeAccordionKey === "2" ? null : "2")
    }
  >
    <h6>Create New Policy</h6>
    <span>{activeAccordionKey === "2" ? "â–²" : "â–¼"}</span>
  </div>

  {activeAccordionKey === "2" && (
    <div className="p-3 border-top">
            <Form onSubmit={handlePolicySubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Data Category</Form.Label>
                    <Form.Control
                      type="text"
                      name="category"
                      value={newPolicy.category}
                      onChange={handleNewPolicyChange}
                      placeholder="e.g., Customer Personal Data"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Retention Period</Form.Label>
                    <Form.Control
                      type="number"
                      name="periodValue"
                      value={newPolicy.periodValue}
                      onChange={handleNewPolicyChange}
                      min="1"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>&nbsp;</Form.Label>
                    <Form.Select
                      name="periodUnit"
                      value={newPolicy.periodUnit}
                      onChange={handleNewPolicyChange}
                    >
                      <option value="Days">Days</option>
                      <option value="Months">Months</option>
                      <option value="Years">Years</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Legal Basis for Retention</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="legalBasis"
                  value={newPolicy.legalBasis}
                  onChange={handleNewPolicyChange}
                  placeholder="e.g., Art. 6(1)(c) - Legal obligation under tax law"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="autoDelete"
                  checked={newPolicy.autoDelete}
                  onChange={handleNewPolicyChange}
                  label="Enable automatic deletion when retention period expires"
                />
              </Form.Group>
              <Button type="submit" variant="primary">Create Policy</Button>
            </Form>
         </div>
  )}
</div>  
  </>
  );
};

export default GDPRRetentionTab;